import {
    CustomQuestionDto,
    MarketplaceAddressDto,
    MarketplaceDto,
    SpecialHourDto
} from 'actions/marketplace/MarketplaceDtos';
import moment from 'moment-timezone';
import { Option } from 'types/Generics';
import {
    IntakeFormCustomQuestion,
    IntakeFormCustomQuestionCategory,
    IntakeFormPreferences
} from 'types/IntakeForm';
import {
    Marketplace,
    MarketplaceAddress,
    MarketplaceDeposit,
    MarketplaceDepositCategory,
    MarketplacePetType,
    MarketplacePetTypes,
    MarketplaceType,
    SpecialHour
} from 'types/Marketplace';
import { OnlineBookingPreferences } from 'types/OnlineBooking';
import { parseJSON } from 'utils/json';

export const convertMarketplace = (marketplaceDto: MarketplaceDto): Marketplace => {
    return {
        id: marketplaceDto.id,
        slug: marketplaceDto.url_slug,
        name: marketplaceDto.business_name,
        logo: marketplaceDto.logo_url,
        phone: marketplaceDto.phone,
        timeZone: marketplaceDto.time_zone,
        businessType: convertToBusinessType(marketplaceDto),

        petTypes: convertToPetTypes(marketplaceDto),

        address:
            marketplaceDto.addresses.length > 0
                ? convertToMarketplaceAddress(marketplaceDto.addresses[0])
                : undefined,

        deposit: convertToDeposit(marketplaceDto),

        onlineBookingPreferences: convertToOnlineBookingPreferences(marketplaceDto),

        intakeFormPreferences: convertToIntakeFormPreferences(marketplaceDto),

        scheduleType: marketplaceDto.schedule_type as Marketplace['scheduleType'],
        schedule: parseJSON(marketplaceDto.range.range, {
            sunday: null,
            monday: null,
            tuesday: null,
            wednesday: null,
            thursday: null,
            friday: null,
            saturday: null
        }),

        specialHours: marketplaceDto.special_hours.map(specialHour =>
            convertToSpecialHour(specialHour)
        ),

        vaccines: convertToVaccines(marketplaceDto),

        // Pets
        catsEnabled: convertIsPetTypeEnabled(marketplaceDto.cats_detail),
        dogsEnabled: convertIsPetTypeEnabled(marketplaceDto.dogs_detail),
        exoticsEnabled: convertIsPetTypeEnabled(marketplaceDto.exotics_detail)
    };
};

const convertToBusinessType = (dto: MarketplaceDto) => {
    if (dto.mobile_business) {
        return MarketplaceType.MOBILE;
    } else if (dto.athome_business) {
        return MarketplaceType.ATHOME;
    } else if (dto.inhome_business) {
        return MarketplaceType.INHOME;
    }

    return MarketplaceType.LOCAL_BUSINESS;
};

const convertToMarketplaceAddress = (addresses: MarketplaceAddressDto): MarketplaceAddress => {
    return {
        id: addresses.id,
        addressLineOne: addresses.address_line_one,
        addressLineTwo: addresses.address_line_two,
        city: addresses.city,
        country: addresses.country,
        zipcode: addresses.zipcode,
        latitude: addresses.latitude,
        longitude: addresses.longitude,
        state: addresses.state
    };
};

const convertToDeposit = (marketplaceDto: MarketplaceDto): MarketplaceDeposit | undefined => {
    if (
        marketplaceDto.deposit_value === MarketplaceDepositCategory.NONE ||
        Number(marketplaceDto.deposit_value) === 0
    ) {
        return undefined;
    }

    return {
        category: marketplaceDto.deposit_category as MarketplaceDepositCategory,
        value: Number(marketplaceDto.deposit_value)
    };
};

const convertToOnlineBookingPreferences = (
    marketplaceDto: MarketplaceDto
): OnlineBookingPreferences => {
    return {
        enabled: marketplaceDto.olb_enabled,
        allowNewClients: marketplaceDto.olb_accept_new_client,
        allowSelectStaff: marketplaceDto.olb_can_select_staff,
        requireBookingRequest: marketplaceDto.olb_booking_request,
        promoImageUrl: marketplaceDto.promo_image_url,
        pricingOption: marketplaceDto.pricing_option as OnlineBookingPreferences['pricingOption'],
        bookingDateLimits: {
            from: marketplaceDto.olb_minimum_lead_time ?? null,
            to: marketplaceDto.olb_maximum_lead_time ?? null
        }
    };
};

const convertToIntakeFormPreferences = (marketplaceDto: MarketplaceDto): IntakeFormPreferences => {
    return {
        enabled: marketplaceDto.intake_form,
        questionsEnabled: marketplaceDto.custom_questions_enabled,
        questions: marketplaceDto.custom_questions.map(question =>
            convertToCustomQuestion(question, marketplaceDto)
        )
    };
};

const convertToSpecialHour = (dto: SpecialHourDto): SpecialHour => {
    return {
        id: dto.id,
        name: dto.name,
        closed: dto.closed,
        businessHours: parseJSON(dto.business_hours, {
            open: '',
            close: ''
        }),
        date: dto.date
    };
};

const convertToVaccines = (marketplaceDto: MarketplaceDto): Array<Option<string>> => {
    return marketplaceDto.pet_requirement_list
        .filter(opt => opt !== 'spay_and_neuter')
        .map(vaccine => ({
            id: vaccine,
            name: vaccine.charAt(0).toUpperCase() + vaccine.slice(1).replace(/[-_]/g, ' ')
        }));
};

const convertIsPetTypeEnabled = (detail: string | undefined): boolean => {
    if ((detail ?? '').length === 0) {
        return false;
    } else {
        const petTypeDetail = JSON.parse(detail ?? '');
        return petTypeDetail.enabled ?? true;
    }
};

const convertToCustomQuestion = (
    dto: CustomQuestionDto,
    marketplace: MarketplaceDto
): IntakeFormCustomQuestion => {
    return {
        id: dto.id,
        isEnabled: dto.enabled,
        isRequired: dto.required,
        order: dto.display_order,
        question: dto.question_text,
        marketplaceId: dto.marketplace_id,
        category: dto.category as IntakeFormCustomQuestionCategory,
        createdAt: moment(dto.created_at).tz(marketplace.time_zone),
        updatedAt: moment(dto.updated_at).tz(marketplace.time_zone)
    };
};

const convertToPetType = (petType: string): MarketplacePetType => {
    const defaultValue = { enabled: false, sizes: [], ages: [], hairTypes: [], hairLengths: [] };

    return parseJSON(petType, defaultValue);
};

const convertToPetTypes = (marketplaceDto: MarketplaceDto): MarketplacePetTypes => {
    const cats: MarketplacePetType = convertToPetType(marketplaceDto.cats_detail);
    const dogs: MarketplacePetType = convertToPetType(marketplaceDto.dogs_detail);
    const exotics: MarketplacePetType = convertToPetType(marketplaceDto.exotics_detail);

    return {
        cats: {
            enabled: cats.enabled,
            sizes: cats.sizes ?? [],
            hairTypes: cats.hairTypes ?? [],
            hairLengths: cats.hairLengths ?? []
        },
        dogs: {
            enabled: dogs.enabled,
            sizes: dogs.sizes ?? [],
            hairTypes: dogs.hairTypes ?? [],
            hairLengths: dogs.hairLengths ?? []
        },
        exotics: {
            enabled: exotics.enabled,
            sizes: exotics.sizes ?? [],
            hairTypes: exotics.hairTypes ?? [],
            hairLengths: exotics.hairLengths ?? []
        }
    };
};
